import { isStreamDebug, streamLog } from "./debug";
import { StreamState } from "./types";

export interface CharDisplay {
  rolling: string;
  interp: string;
}

export interface CharStreamResult {
  /** Keyed by segment id. */
  display: Record<string, CharDisplay>;
  animating: boolean;
  /** The single segment currently revealing (carries the streaming caret); null when idle. */
  animatingId: string | null;
}

export interface CharStreamOptions {
  step?: number;
  backlogJump?: number;
}

export class CharStream {
  private shownRolling = new Map<string, number>();
  private shownInterp = new Map<string, number>();
  private step: number;
  private backlogJump: number;

  constructor(opts: CharStreamOptions = {}) {
    this.step = opts.step ?? 2;
    this.backlogJump = opts.backlogJump ?? 200;
  }

  reset(): void {
    this.shownRolling.clear();
    this.shownInterp.clear();
  }

  private clamp(map: Map<string, number>, id: string, len: number, field: "rolling" | "interp"): number {
    let shown = map.get(id) ?? 0;
    if (shown > len) {
      if (isStreamDebug()) {
        streamLog("char", "WARN canonical value shrank (possible replace/restart)", {
          id,
          field,
          shown,
          targetLen: len
        });
      }
      shown = len;
    }
    return shown;
  }

  private advanceField(
    map: Map<string, number>,
    id: string,
    target: string,
    mode: "animate" | "full" | "hold",
    field: "rolling" | "interp"
  ): string {
    const len = target.length;
    let shown = this.clamp(map, id, len, field);
    if (mode === "full") {
      shown = len;
    } else if (mode === "animate" && shown < len) {
      const remaining = len - shown;
      const snap = remaining > this.backlogJump;
      shown += snap ? remaining : this.step;
      if (shown > len) shown = len;
    }
    
    map.set(id, shown);
    return target.slice(0, shown);
  }

  advance(state: StreamState, opts: CharStreamOptions = {}): CharStreamResult {
    this.step = opts.step ?? this.step;
    this.backlogJump = opts.backlogJump ?? this.backlogJump;

    const display: Record<string, CharDisplay> = {};
    const liveIds = new Set(Object.keys(state.segments));

    // Pick the single active segment: the EARLIEST (lowest arrival) non-historical
    // segment that still has unrevealed characters in either field. Only this
    // segment animates; earlier ones are already fully revealed, later ones are
    // held frozen — so exactly one card streams at a time, in order.
    let activeId: string | null = null;
    let activeArrival = Infinity;
    for (const segId of Object.keys(state.segments)) {
      const seg = state.segments[segId];
      if (seg.historical) continue;
      const shownR = this.clamp(this.shownRolling, segId, seg.rolling.length, "rolling");
      const shownI = this.clamp(this.shownInterp, segId, seg.interp.length, "interp");
      const hasUnrevealed = shownR < seg.rolling.length || shownI < seg.interp.length;
      if (hasUnrevealed && seg.arrival < activeArrival) {
        activeArrival = seg.arrival;
        activeId = segId;
      }
    }

    for (const segId of Object.keys(state.segments)) {
      const seg = state.segments[segId];
      // History always renders in full and never animates. A live segment either
      // animates (the single active one) or holds at its current reveal count.
      const mode: "animate" | "full" | "hold" = seg.historical
        ? "full"
        : segId === activeId
        ? "animate"
        : "hold";
      const rolling = this.advanceField(this.shownRolling, segId, seg.rolling, mode, "rolling");
      const interp = this.advanceField(this.shownInterp, segId, seg.interp, mode, "interp");
      display[segId] = { rolling, interp };
    }

    for (const id of Array.from(this.shownRolling.keys())) if (!liveIds.has(id)) this.shownRolling.delete(id);
    for (const id of Array.from(this.shownInterp.keys())) if (!liveIds.has(id)) this.shownInterp.delete(id);

    return { display, animating: activeId !== null, animatingId: activeId };
  }
}
